/**
 * SIG Legacy Clone API Client
 *
 * Implements the Amplify wrapper pattern for the SIG Clone backend endpoints.
 * Uses nova API for all requests with impersonation header support.
 *
 * @see packages/serverless/api/docs/monumenta-sig-clone-COMPLETE.md
 */

import { get, post, put, del } from '@/api/lib/amplify-wrapper'
import { createImpersonatedApiHeaders } from '@/lib/services/api-utils'
import type {
  ClientWithGrouping,
  BillingReportFilters,
  BillingReportRow,
  ConsolidatedReportFilters,
  ConsolidatedReportRow,
  CostsReportFilters,
  CostsReportRow,
  ClientProfitabilityFilters,
  ClientProfitabilityRow,
  CashflowEntry,
  TechnicalNote,
  TimesheetImport,
  ClientDropdownItem,
  JobDropdownItem,
} from '@/types/sig-clone'

const API_NAME = 'nova'

/**
 * Helper function to serialize data for API requests
 * Converts Date objects to ISO strings for JSON serialization
 */
const serializeData = (data: unknown) => {
  return JSON.parse(JSON.stringify(data, (_key, value) => {
    if (value instanceof Date) {
      return value.toISOString()
    }
    return value
  }))
}

/**
 * Helper function to extract pagination metadata from response headers
 */
const extractPaginationMetadata = (headers: Headers, itemsLength: number): { nextToken?: string; itemCount: number } => {
  const nextToken = headers?.get('x-next-token') || headers?.get('X-Next-Token')
  const itemCount = headers?.get('x-item-count') || headers?.get('X-Item-Count')

  return {
    nextToken: nextToken || undefined,
    itemCount: itemCount ? parseInt(itemCount) : itemsLength
  }
}

/**
 * Map API errors to user-friendly messages (Portuguese)
 */
const mapApiError = (error: unknown): string => {
  const err = error as { response?: { status?: number } }
  if (err.response?.status === 400) return 'Entrada inválida. Por favor, verifique seus dados.'
  if (err.response?.status === 403) return 'Você não tem permissão para realizar esta ação.'
  if (err.response?.status === 404) return 'Recurso não encontrado.'
  if (err.response?.status === 500) return 'Erro do servidor. Por favor, tente novamente mais tarde.'
  return 'Ocorreu um erro inesperado. Por favor, tente novamente.'
}

// ============================================================================
// MASTER DATA - Clients
// ============================================================================

/**
 * List all clients with grouping information
 */
export const listClients = async (): Promise<ClientWithGrouping[]> => {
  try {
    const restOperation = get({
      apiName: API_NAME,
      path: '/monumenta/sig-clone/clients',
      options: {
        headers: createImpersonatedApiHeaders()
      }
    })

    const response = await restOperation.response
    const data = await response.body.json()
    return data as unknown as ClientWithGrouping[]
  } catch (error) {
    console.error('Error listing clients:', error)
    throw new Error(mapApiError(error))
  }
}

/**
 * Get a single client by ID
 */
export const getClient = async (id: number): Promise<ClientWithGrouping> => {
  try {
    const restOperation = get({
      apiName: API_NAME,
      path: `/monumenta/sig-clone/clients/${id}`,
      options: {
        headers: createImpersonatedApiHeaders()
      }
    })

    const response = await restOperation.response
    const data = await response.body.json()
    return data as unknown as ClientWithGrouping
  } catch (error) {
    console.error(`Error getting client ${id}:`, error)
    throw new Error(mapApiError(error))
  }
}

/**
 * Update client grouping (parent client)
 */
export const updateClient = async (
  id: number,
  clientePaiId: number | null
): Promise<ClientWithGrouping> => {
  try {
    const restOperation = put({
      apiName: API_NAME,
      path: `/monumenta/sig-clone/clients/${id}`,
      options: {
        body: serializeData({ clientePaiId }),
        headers: createImpersonatedApiHeaders()
      }
    })

    const response = await restOperation.response
    const data = await response.body.json()
    return data as unknown as ClientWithGrouping
  } catch (error) {
    console.error(`Error updating client ${id}:`, error)
    throw new Error(mapApiError(error))
  }
}

// ============================================================================
// REPORTS
// ============================================================================

/**
 * Generate billing report (Faturamento)
 */
export const getBillingReport = async (
  filters: BillingReportFilters
): Promise<BillingReportRow[]> => {
  try {
    const restOperation = post({
      apiName: API_NAME,
      path: '/monumenta/sig-clone/reports/billing',
      options: {
        body: serializeData(filters),
        headers: createImpersonatedApiHeaders()
      }
    })

    const response = await restOperation.response
    const data = await response.body.json()
    return data as unknown as BillingReportRow[]
  } catch (error) {
    console.error('Error getting billing report:', error)
    throw new Error(mapApiError(error))
  }
}

/**
 * Generate consolidated report (Por Período)
 */
export const getConsolidatedReport = async (
  filters: ConsolidatedReportFilters
): Promise<ConsolidatedReportRow[]> => {
  try {
    const restOperation = post({
      apiName: API_NAME,
      path: '/monumenta/sig-clone/reports/consolidated',
      options: {
        body: serializeData(filters),
        headers: createImpersonatedApiHeaders()
      }
    })

    const response = await restOperation.response
    const data = await response.body.json()
    return data as unknown as ConsolidatedReportRow[]
  } catch (error) {
    console.error('Error getting consolidated report:', error)
    throw new Error(mapApiError(error))
  }
}

/**
 * Generate costs report (Custos)
 */
export const getCostsReport = async (
  filters: CostsReportFilters
): Promise<CostsReportRow[]> => {
  try {
    const restOperation = post({
      apiName: API_NAME,
      path: '/monumenta/sig-clone/reports/costs',
      options: {
        body: serializeData(filters),
        headers: createImpersonatedApiHeaders()
      }
    })

    const response = await restOperation.response
    const data = await response.body.json()
    return data as unknown as CostsReportRow[]
  } catch (error) {
    console.error('Error getting costs report:', error)
    throw new Error(mapApiError(error))
  }
}

/**
 * Generate client profitability report (Por Clientes)
 */
export const getClientProfitabilityReport = async (
  filters: ClientProfitabilityFilters
): Promise<ClientProfitabilityRow[]> => {
  try {
    const restOperation = post({
      apiName: API_NAME,
      path: '/monumenta/sig-clone/reports/client',
      options: {
        body: serializeData(filters),
        headers: createImpersonatedApiHeaders()
      }
    })

    const response = await restOperation.response
    const data = await response.body.json()
    return data as unknown as ClientProfitabilityRow[]
  } catch (error) {
    console.error('Error getting client profitability report:', error)
    throw new Error(mapApiError(error))
  }
}

/**
 * Generate client BU profitability report (Por Clientes/BU)
 */
export const getClientBUReport = async (
  filters: ClientProfitabilityFilters
): Promise<ClientProfitabilityRow[]> => {
  try {
    const restOperation = post({
      apiName: API_NAME,
      path: '/monumenta/sig-clone/reports/client-bu',
      options: {
        body: serializeData(filters),
        headers: createImpersonatedApiHeaders()
      }
    })

    const response = await restOperation.response
    const data = await response.body.json()
    return data as unknown as ClientProfitabilityRow[]
  } catch (error) {
    console.error('Error getting client BU report:', error)
    throw new Error(mapApiError(error))
  }
}

// ============================================================================
// CASHFLOW
// ============================================================================

export interface CashflowListParams {
  limit?: number
  nextToken?: string
  dataInicial?: string
  dataFinal?: string
}

export interface PaginationResult<T> {
  items: T[]
  pagination: {
    nextToken?: string
    itemCount: number
  }
}

/**
 * List cashflow entries with pagination
 */
export const listCashflowEntries = async (
  params?: CashflowListParams
): Promise<PaginationResult<CashflowEntry>> => {
  try {
    const queryParams = new URLSearchParams()
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.nextToken) queryParams.append('nextToken', params.nextToken)
    if (params?.dataInicial) queryParams.append('dataInicial', params.dataInicial)
    if (params?.dataFinal) queryParams.append('dataFinal', params.dataFinal)

    const path = '/monumenta/sig-clone/cashflow/entries' +
      (queryParams.toString() ? `?${queryParams.toString()}` : '')

    const restOperation = get({
      apiName: API_NAME,
      path,
      options: {
        headers: createImpersonatedApiHeaders()
      }
    })

    const response = await restOperation.response
    const data = await response.body.json()
    const items = data as unknown as CashflowEntry[]

    const pagination = extractPaginationMetadata(response.headers as unknown as Headers, items.length)

    return {
      items,
      pagination
    }
  } catch (error) {
    console.error('Error listing cashflow entries:', error)
    throw new Error(mapApiError(error))
  }
}

/**
 * Create cashflow entry
 */
export const createCashflowEntry = async (
  data: Omit<CashflowEntry, 'id'>
): Promise<CashflowEntry> => {
  try {
    const restOperation = post({
      apiName: API_NAME,
      path: '/monumenta/sig-clone/cashflow/entries',
      options: {
        body: serializeData(data),
        headers: createImpersonatedApiHeaders()
      }
    })

    const response = await restOperation.response
    const responseData = await response.body.json()
    return responseData as unknown as CashflowEntry
  } catch (error) {
    console.error('Error creating cashflow entry:', error)
    throw new Error(mapApiError(error))
  }
}

/**
 * Update cashflow entry
 */
export const updateCashflowEntry = async (
  id: number,
  updates: Partial<CashflowEntry>
): Promise<CashflowEntry> => {
  try {
    const restOperation = put({
      apiName: API_NAME,
      path: `/monumenta/sig-clone/cashflow/entries/${id}`,
      options: {
        body: serializeData(updates),
        headers: createImpersonatedApiHeaders()
      }
    })

    const response = await restOperation.response
    const data = await response.body.json()
    return data as unknown as CashflowEntry
  } catch (error) {
    console.error(`Error updating cashflow entry ${id}:`, error)
    throw new Error(mapApiError(error))
  }
}

/**
 * Delete cashflow entry
 */
export const deleteCashflowEntry = async (id: number): Promise<void> => {
  try {
    const restOperation = del({
      apiName: API_NAME,
      path: `/monumenta/sig-clone/cashflow/entries/${id}`,
      options: {
        headers: createImpersonatedApiHeaders()
      }
    })

    await restOperation.response
  } catch (error) {
    console.error(`Error deleting cashflow entry ${id}:`, error)
    throw new Error(mapApiError(error))
  }
}

// ============================================================================
// TECHNICAL NOTES
// ============================================================================

/**
 * List all technical notes
 */
export const listTechnicalNotes = async (): Promise<TechnicalNote[]> => {
  try {
    const restOperation = get({
      apiName: API_NAME,
      path: '/monumenta/sig-clone/technical-notes',
      options: {
        headers: createImpersonatedApiHeaders()
      }
    })

    const response = await restOperation.response
    const data = await response.body.json()
    return data as unknown as TechnicalNote[]
  } catch (error) {
    console.error('Error listing technical notes:', error)
    throw new Error(mapApiError(error))
  }
}

/**
 * Get a single technical note by ID
 */
export const getTechnicalNote = async (id: number): Promise<TechnicalNote> => {
  try {
    const restOperation = get({
      apiName: API_NAME,
      path: `/monumenta/sig-clone/technical-notes/${id}`,
      options: {
        headers: createImpersonatedApiHeaders()
      }
    })

    const response = await restOperation.response
    const data = await response.body.json()
    return data as unknown as TechnicalNote
  } catch (error) {
    console.error(`Error getting technical note ${id}:`, error)
    throw new Error(mapApiError(error))
  }
}

/**
 * Create technical note
 */
export const createTechnicalNote = async (
  data: Omit<TechnicalNote, 'id'>
): Promise<TechnicalNote> => {
  try {
    const restOperation = post({
      apiName: API_NAME,
      path: '/monumenta/sig-clone/technical-notes',
      options: {
        body: serializeData(data),
        headers: createImpersonatedApiHeaders()
      }
    })

    const response = await restOperation.response
    const responseData = await response.body.json()
    return responseData as unknown as TechnicalNote
  } catch (error) {
    console.error('Error creating technical note:', error)
    throw new Error(mapApiError(error))
  }
}

/**
 * Update technical note
 */
export const updateTechnicalNote = async (
  id: number,
  updates: Partial<TechnicalNote>
): Promise<TechnicalNote> => {
  try {
    const restOperation = put({
      apiName: API_NAME,
      path: `/monumenta/sig-clone/technical-notes/${id}`,
      options: {
        body: serializeData(updates),
        headers: createImpersonatedApiHeaders()
      }
    })

    const response = await restOperation.response
    const data = await response.body.json()
    return data as unknown as TechnicalNote
  } catch (error) {
    console.error(`Error updating technical note ${id}:`, error)
    throw new Error(mapApiError(error))
  }
}

/**
 * Delete technical note
 */
export const deleteTechnicalNote = async (id: number): Promise<void> => {
  try {
    const restOperation = del({
      apiName: API_NAME,
      path: `/monumenta/sig-clone/technical-notes/${id}`,
      options: {
        headers: createImpersonatedApiHeaders()
      }
    })

    await restOperation.response
  } catch (error) {
    console.error(`Error deleting technical note ${id}:`, error)
    throw new Error(mapApiError(error))
  }
}

// ============================================================================
// TIMESHEET
// ============================================================================

export interface TimesheetListParams {
  limit?: number
  nextToken?: string
}

/**
 * List timesheet imports with pagination
 */
export const listTimesheetImports = async (
  params?: TimesheetListParams
): Promise<PaginationResult<TimesheetImport>> => {
  try {
    const queryParams = new URLSearchParams()
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.nextToken) queryParams.append('nextToken', params.nextToken)

    const path = '/monumenta/sig-clone/timesheet' +
      (queryParams.toString() ? `?${queryParams.toString()}` : '')

    const restOperation = get({
      apiName: API_NAME,
      path,
      options: {
        headers: createImpersonatedApiHeaders()
      }
    })

    const response = await restOperation.response
    const data = await response.body.json()
    const items = data as unknown as TimesheetImport[]

    const pagination = extractPaginationMetadata(response.headers as unknown as Headers, items.length)

    return {
      items,
      pagination
    }
  } catch (error) {
    console.error('Error listing timesheet imports:', error)
    throw new Error(mapApiError(error))
  }
}

/**
 * Import timesheet Excel file
 */
export const importTimesheet = async (formData: FormData): Promise<TimesheetImport> => {
  try {
    const restOperation = post({
      apiName: API_NAME,
      path: '/monumenta/sig-clone/timesheet/import',
      options: {
        body: formData,
        headers: {
          ...createImpersonatedApiHeaders(),
          // Don't set Content-Type - let browser set it with boundary
        }
      }
    })

    const response = await restOperation.response
    const data = await response.body.json()
    return data as unknown as TimesheetImport
  } catch (error) {
    console.error('Error importing timesheet:', error)
    throw new Error(mapApiError(error))
  }
}

// ============================================================================
// AJAX UTILITIES (Dropdowns)
// ============================================================================

/**
 * Get clients for dropdown
 */
export const getClientsDropdown = async (): Promise<ClientDropdownItem[]> => {
  try {
    const restOperation = get({
      apiName: API_NAME,
      path: '/monumenta/sig-clone/ajax/clients',
      options: {
        headers: createImpersonatedApiHeaders()
      }
    })

    const response = await restOperation.response
    const data = await response.body.json()
    return data as unknown as ClientDropdownItem[]
  } catch (error) {
    console.error('Error getting clients dropdown:', error)
    throw new Error(mapApiError(error))
  }
}

/**
 * Get jobs for dropdown
 */
export const getJobsDropdown = async (): Promise<JobDropdownItem[]> => {
  try {
    const restOperation = get({
      apiName: API_NAME,
      path: '/monumenta/sig-clone/ajax/jobs',
      options: {
        headers: createImpersonatedApiHeaders()
      }
    })

    const response = await restOperation.response
    const data = await response.body.json()
    return data as unknown as JobDropdownItem[]
  } catch (error) {
    console.error('Error getting jobs dropdown:', error)
    throw new Error(mapApiError(error))
  }
}
